import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ToastContext, type ToastApi, type ToastAction, type ToastKind } from '../lib/toast-context';
import { I } from '../lib/icons';

type ToastEntry = { id: number; kind: ToastKind; message: string; action?: ToastAction };

const AUTO_DISMISS_MS = 4000;
// Action-Toasts (z.B. Undo) bleiben länger, damit die Aktion klickbar bleibt.
const ACTION_DISMISS_MS = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, action?: ToastAction) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, kind, message, action }]);
      window.setTimeout(() => dismiss(id), action ? ACTION_DISMISS_MS : AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  // Stable API ref — push-Identity ändert sich nur wenn dismiss-Identity ändert
  // (und das ist via useCallback stabil), daher 1× erzeugt.
  const api = useRef<ToastApi>({
    success: (m) => push('success', m),
    error: (m) => push('error', m),
    info: (m) => push('info', m),
    withAction: (m, action, kind = 'info') => push(kind, m, action),
  });

  // Falls push neu wird (theoretisch), api-Methoden re-binden
  useEffect(() => {
    api.current = {
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
      withAction: (m, action, kind = 'info') => push(kind, m, action),
    };
  }, [push]);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} entry={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Token-Paletten pro Kind. error/danger nutzt Tailwind-`red-*` (Danger-
// Konvention im Projekt — kein Danger-Token im @theme).
const TOAST_PALETTE: Record<ToastKind, string> = {
  success: 'bg-brand-green-700 text-brand-cream border-brand-green-700',
  error: 'bg-red-600 text-white border-red-700',
  info: 'bg-white text-brand-green-800 border-ink-200',
};

function ToastItem({ entry, onDismiss }: { entry: ToastEntry; onDismiss: () => void }) {
  // Live-Region-Rolle auf dem nicht-interaktiven Wrapper-<div>: error ist
  // assertive (`alert`), success/info polite (`status`). Die Rolle gehört
  // NICHT auf den Dismiss-<button> — das würde dessen Button-Semantik im
  // Accessibility-Tree überschreiben (Codex Spec-Eval R1).
  return (
    <div
      role={entry.kind === 'error' ? 'alert' : 'status'}
      className={`gf-toast-enter pointer-events-auto flex items-start gap-3 max-w-[380px] px-4 py-3 rounded-lg border shadow-pop ${TOAST_PALETTE[entry.kind]}`}
    >
      <span className="flex-1 min-w-0 break-words text-sm">{entry.message}</span>
      {entry.action && (
        <button
          type="button"
          onClick={() => {
            entry.action!.onClick();
            onDismiss();
          }}
          className="shrink-0 rounded px-1.5 py-0.5 text-sm font-semibold underline underline-offset-2 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
        >
          {entry.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Benachrichtigung schließen"
        className="shrink-0 -mr-1 -mt-0.5 rounded p-0.5 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
      >
        <I.close size={16} />
      </button>
    </div>
  );
}
